import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatType } from '../../api/chats';
import { subscribeToPresence } from '../../utils/presenceSubscription';
import { useAvatarUrl } from '../../hooks/useAvatarUrl';
import OnlineStatusIndicator from '../controls/OnlineStatusIndicator';
import '../../css/chats/ChatListItem.css';
import HrnbImage from "../controls/HrnbImage";

/**
 * Элемент списка чатов
 * Отображает: avatar, lastMessage, time, unread
 *
 * Данные профилей, баттлов и presence предзагружаются в ChatsLayout
 * через chatDataCache и передаются через props.
 * Здесь — только подписка на WS-обновления presence для DIRECT-чатов.
 *
 * Поддержка свайпа влево для архивации.
 */
export default function ChatListItem({
    chat,
    currentUser,
    onClick,
    onTogglePin,
    onArchive,
    isActive = false,
    isFocused = false,
    className = "",
    profileData = null,
    battleData = null,
    presenceData = null
}) {
    const { id, title, chatType, lastMessage, lastMessageAt, lastMessagePreview, unreadCount, participants, battleId, isPinned, isArchived } = chat;
    const navigate = useNavigate();

    // Клик по аватару: DIRECT → профиль, BATTLE_SUPPORT → баттл, остальное → чат
    const handleAvatarClick = useCallback((e) => {
        e.stopPropagation();
        if (chatType === ChatType.DIRECT) {
            const otherProfileId = participants?.find(pId => pId !== currentUser?.profileId && pId !== currentUser?.profiles?.[0]?.id) || participants?.[0];
            if (otherProfileId) navigate(`/profile/${otherProfileId}`);
        } else if ((chatType === ChatType.BATTLE_SUPPORT || chatType === ChatType.BATTLE_GROUP || chatType === ChatType.BATTLE_LIVE || chatType === ChatType.BATTLE_PARTICIPANTS) && battleId) {
            navigate(`/battles/${battleId}`);
        } else {
            onClick();
        }
    }, [chatType, participants, currentUser, navigate, battleId, onClick]);

    // V2: lastMessagePreview { content, senderId, senderName, sentAt }
    const previewContent = lastMessagePreview?.content || lastMessage;
    const previewTime = lastMessagePreview?.sentAt || lastMessageAt;
    const previewSenderName = lastMessagePreview?.senderName;

    // Определяем тип чата для отображения
    const chatTypeLabel = {
        [ChatType.DIRECT]: 'Личное сообщение',
        [ChatType.GROUP]: 'Групповой чат',
        [ChatType.BATTLE_GROUP]: 'Чат баттла',
        [ChatType.DUEL_GROUP]: 'Чат дуэли',
        [ChatType.BATTLE_SUPPORT]: 'Поддержка баттла',
        [ChatType.BATTLE_LIVE]: 'Live-чат баттла',
        [ChatType.BATTLE_PARTICIPANTS]: 'Чат участников баттла'
    }[chatType] || 'Чат';

    // Presence из предзагруженных данных, обновляемое через WS
    const profileUserId = profileData?.userId;
    const initialPresence = profileUserId ? presenceData?.get(profileUserId) : null;
    const [isOnline, setIsOnline] = useState(initialPresence?.isOnline ?? false);
    const [lastActivity, setLastActivity] = useState(initialPresence?.lastActivity ?? null);

    // Обновляем при смене предзагруженных данных
    useEffect(() => {
        if (profileUserId && presenceData) {
            const p = presenceData.get(profileUserId);
            if (p) {
                setIsOnline(p.isOnline);
                setLastActivity(p.lastActivity);
            }
        }
    }, [profileUserId, presenceData]);

    // Подписка на WebSocket обновления presence (только для DIRECT)
    // Используем дедупликатор — если ChatWindow уже подписан на того же
    // пользователя, новая STOMP-подписка не создаётся.
    useEffect(() => {
        if (!profileUserId) return;

        const subscription = subscribeToPresence(profileUserId, (presence) => {
            setIsOnline(presence.isOnline);
            setLastActivity(presence.lastActivity);
        });

        return () => {
            if (subscription) subscription.unsubscribe();
        };
    }, [profileUserId]);

    // === Свайп для архивации ===
    const itemRef = useRef(null);
    const [swiped, setSwiped] = useState(false);
    const touchStartX = useRef(0);
    const touchStartY = useRef(0);
    const SWIPE_THRESHOLD = 80;

    const handleTouchStart = useCallback((e) => {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
    }, []);

    const handleTouchEnd = useCallback((e) => {
        const dx = touchStartX.current - e.changedTouches[0].clientX;
        const dy = touchStartY.current - e.changedTouches[0].clientY;
        // Только горизонтальный свайп влево, без сильного вертикального смещения
        if (dx > SWIPE_THRESHOLD && Math.abs(dy) < 50) {
            if (onArchive) {
                onArchive(id);
            } else {
                setSwiped(prev => !prev);
            }
        } else if (dx < -SWIPE_THRESHOLD && Math.abs(dy) < 50) {
            // Свайп вправо — закрываем
            setSwiped(false);
        }
    }, [id, onArchive]);

    // Сброс свайпа при смене чата
    useEffect(() => {
        setSwiped(false);
    }, [id]);

    // Для DIRECT чатов показываем имя собеседника, для BATTLE_SUPPORT - название баттла
    const displayName = chatType === ChatType.DIRECT
        ? (profileData?.displayName || `${chatTypeLabel} #${id}`)
        : chatType === ChatType.BATTLE_SUPPORT
            ? (battleData?.title || title || `Баттл #${battleId?.slice(0, 8) || id}`)
            : (title || `${chatTypeLabel} #${id}`);

    // Аватар: для DIRECT — blob URL собеседника, для BATTLE_SUPPORT — обложка баттла
    const avatarBlobUrl = useAvatarUrl(profileData?.avatarId);
    const hasAvatar = chatType === ChatType.DIRECT
        ? avatarBlobUrl
        : chatType === ChatType.BATTLE_SUPPORT
            ? (battleData?.coverUrl || null)
            : null;
    const avatar = hasAvatar || null;
    const avatarFallback = displayName?.charAt(0)?.toUpperCase() || '?';

    // V2: время из lastMessagePreview.sentAt или lastMessageAt
    const timeStr = previewTime
        ? new Date(previewTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : '';

    return (
        <div
            ref={itemRef}
            className={`chat-list-item ${className} ${unreadCount > 0 ? 'chat-list-item--unread' : ''} ${isActive ? 'chat-list-item--active' : ''} ${isFocused ? 'chat-list-item--focused' : ''} ${isPinned ? 'chat-list-item--pinned' : ''} ${isArchived ? 'chat-list-item--archived' : ''} ${swiped ? 'chat-list-item--swiped' : ''}`}
            onClick={onClick}
            tabIndex={isFocused ? 0 : -1}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            <div className="chat-list-item__content-wrap">
                <div className="chat-list-item__avatar">
                    {avatar ? (
                        <HrnbImage
                            src={avatar}
                            alt={displayName}
                            className="chat-list-item__avatar-img chat-list-item__avatar-link"
                            rounded={chatType !== ChatType.BATTLE_SUPPORT}
                            size={48}
                            onClick={handleAvatarClick}
                        />
                    ) : (
                        <div
                            className="chat-list-item__avatar-fallback"
                            onClick={handleAvatarClick}
                            title={displayName}
                        >
                            {avatarFallback}
                        </div>
                    )}
                </div>
                <div className="chat-list-item__content">
                    <div className="chat-list-item__header">
                        <span className="chat-list-item__name">
                            {isPinned && <span className="chat-list-item__pin-icon" title="Закреплён">📌</span>}
                            {displayName}
                            {chatType === ChatType.DIRECT && profileUserId && (
                                <OnlineStatusIndicator
                                    isOnline={isOnline}
                                    lastActivity={lastActivity}
                                    size="sm"
                                    showLabel={false}
                                />
                            )}
                        </span>
                        <div className="chat-list-item__header-right">
                            {onTogglePin && (
                                <button
                                    type="button"
                                    className="chat-list-item__pin-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onTogglePin();
                                    }}
                                    title={isPinned ? 'Открепить' : 'Закрепить'}
                                >
                                    {isPinned ? '📍' : '📌'}
                                </button>
                            )}
                            {onArchive && (
                                <button
                                    type="button"
                                    className="chat-list-item__archive-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onArchive(id);
                                    }}
                                    title={isArchived ? 'Разархивировать' : 'Архивировать'}
                                >
                                    {isArchived ? '📁' : '📦'}
                                </button>
                            )}
                            <span className="chat-list-item__time">{timeStr}</span>
                        </div>
                    </div>
                    <div className="chat-list-item__message">
                        {previewContent ? (
                            <>
                                {/* V2: для групповых чатов показываем имя отправителя в превью */}
                                {previewSenderName && chatType !== ChatType.DIRECT && (
                                    <span className="chat-list-item__preview-sender">{previewSenderName}:</span>
                                )}
                                <span className="chat-list-item__message-text">{previewContent}</span>
                                {unreadCount > 0 && (
                                    <span className="chat-list-item__badge">{unreadCount}</span>
                                )}
                            </>
                        ) : (
                            <span className="chat-list-item__no-messages">Нет сообщений</span>
                        )}
                    </div>
                </div>
            </div>
            {onArchive && (
                <div className="chat-list-item__swipe-action">
                    <button
                        type="button"
                        className="chat-list-item__archive-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onArchive(id);
                        }}
                        title={isArchived ? 'Разархивировать' : 'Архивировать'}
                    >
                        {isArchived ? '📁' : '📦'}
                    </button>
                </div>
            )}
        </div>
    );
}
